import React, { useState, useMemo } from 'react';
import { Inbox, X, Sparkles, RefreshCw, AlertCircle, Check, Info, Scale, Bot, Users, Building2, Wrench, FileText, Eye, Trash2, EyeOff } from 'lucide-react';
import { useEmailsPendentes } from '../hooks/useEmailsPendentes';
import { useAuth } from '../hooks/useAuth';
import { useAreas } from '../hooks/useAreas';
import { useOfertas } from '../hooks/useOfertas';
import { useContacts } from '../hooks/useContacts';
import { formatDate } from '../utils/dateHelpers';
import EmailDetailDrawer from './EmailDetailDrawer';
import AIContextChat from './ai/AIContextChat';
import { supabase } from '../supabaseClient';
import useBulkSelection from './shared/useBulkSelection';
import BulkActionBar from './shared/BulkActionBar';
import SelectAllCheckbox from './shared/SelectAllCheckbox';
import FilterChips from './shared/FilterChips';
import BulkVincularEmpresaModal from './BulkVincularEmpresaModal';

const ALL_FILTER = '__all';
const OUTROS_FILTER = '__outros';
const TOP_DOMAINS = 8;
const BULK_CHUNK = 50;

// Extrai domínio de um from_email. Ex: "john@google.com" → "google.com"
function extractDomain(fromEmail) {
  if (!fromEmail) return '';
  const m = String(fromEmail).match(/@([^>\s]+)/);
  return m ? m[1].toLowerCase().trim() : '';
}

// Domínios internos Aston — lado que NUNCA é a contraparte do negócio.
// PONTO ÚNICO DE MUDANÇA (SPEC §6 / dívida D-A2). Medição de 2026-06 confirmou
// que são só estes dois. TODO: externalizar para tabela de config quando houver
// mais colaboradores com domínio externo.
export const ASTON_INTERNAL_DOMAINS = ['astoncapital.com.br', 'spareas.com.br'];

const isInternalDomain = (dom) => !!dom && ASTON_INTERNAL_DOMAINS.includes(dom);

/**
 * Resolve a contraparte (empresa do lado não-Aston) de um email, decidindo a
 * direção pelo domínio do remetente. Função PURA, sem DML. SPEC §2.
 *
 * Retorna { tipo:'vincular', empresa_id, empresa_nome }
 *      ou { tipo:'pulado', categoria, motivo }.
 * categoria ∈ 'ja_vinculado' | 'sem_empresa' | 'ambiguo' (taxonomia SPEC §0).
 *
 * @param {object} email — objeto de emails_gmail em memória (from_email, to_emails, empresa_id).
 * @param {Map<string,{empresa_id,empresa_nome}>} dominioMap — domínio(lower) → empresa.
 */
function resolverContraparteEmpresa(email, dominioMap) {
  // Já vinculado: nunca sobrescrever vínculo existente em lote (SPEC §5).
  if (email?.empresa_id) {
    return { tipo: 'pulado', categoria: 'ja_vinculado', motivo: 'já vinculado' };
  }

  const fromDom = extractDomain(email?.from_email);
  if (!fromDom) {
    return { tipo: 'pulado', categoria: 'ambiguo', motivo: 'sem remetente' };
  }

  if (!isInternalDomain(fromDom)) {
    // INBOUND — contraparte = remetente.
    const hit = dominioMap.get(fromDom);
    if (hit) return { tipo: 'vincular', empresa_id: hit.empresa_id, empresa_nome: hit.empresa_nome };
    return { tipo: 'pulado', categoria: 'sem_empresa', motivo: 'remetente sem empresa mapeada' };
  }

  // OUTBOUND — contraparte = destinatário externo. CC é ignorado (SPEC §2).
  const tos = Array.isArray(email?.to_emails) ? email.to_emails : [];
  const empresasResolvidas = new Map(); // empresa_id → empresa_nome (SET distinto)
  let temDestinatarioExterno = false;
  for (const t of tos) {
    const d = extractDomain(t);
    if (!d || isInternalDomain(d)) continue;
    temDestinatarioExterno = true;
    const hit = dominioMap.get(d);
    if (hit) empresasResolvidas.set(hit.empresa_id, hit.empresa_nome);
  }

  if (!temDestinatarioExterno) {
    return { tipo: 'pulado', categoria: 'ambiguo', motivo: 'sem destinatário' };
  }
  if (empresasResolvidas.size === 1) {
    const [empresa_id, empresa_nome] = empresasResolvidas.entries().next().value;
    return { tipo: 'vincular', empresa_id, empresa_nome };
  }
  if (empresasResolvidas.size === 0) {
    return { tipo: 'pulado', categoria: 'sem_empresa', motivo: 'destinatário sem empresa mapeada' };
  }
  return { tipo: 'pulado', categoria: 'ambiguo', motivo: 'múltiplas empresas no destinatário — ambíguo' };
}

/**
 * Calcula o preview da ação de lote a partir de um snapshot de emails e o mapa
 * domínio→empresa. PURO, sem DML — alimenta o gate de confirmação. SPEC §4.2.
 * Exportado para reuso pela página de triage (Inbox 2.0 / ADR-029).
 */
export function computeVinculoPreview(emails, dominioMap) {
  const gruposMap = new Map(); // empresa_id → { empresa_id, empresa_nome, emailIds, amostras }
  const pulados = [];

  for (const email of emails) {
    const r = resolverContraparteEmpresa(email, dominioMap);
    if (r.tipo === 'vincular') {
      let g = gruposMap.get(r.empresa_id);
      if (!g) {
        g = { empresa_id: r.empresa_id, empresa_nome: r.empresa_nome, emailIds: [], amostras: [] };
        gruposMap.set(r.empresa_id, g);
      }
      g.emailIds.push(email.id);
      if (g.amostras.length < 2 && email.subject) g.amostras.push(email.subject);
    } else {
      pulados.push({
        emailId: email.id,
        categoria: r.categoria,
        motivo: r.motivo,
        subject: email.subject || '(sem assunto)',
      });
    }
  }

  const grupos = Array.from(gruposMap.values())
    .map((g) => ({ ...g, count: g.emailIds.length }))
    .sort((a, b) => b.count - a.count);
  const vincular_total = grupos.reduce((acc, g) => acc + g.count, 0);

  return {
    grupos,
    pulados,
    resumo: {
      vincular_total,
      grupos_count: grupos.length,
      pulados_total: pulados.length,
    },
  };
}

const TIPOS_REMETENTE = {
  humano_interno: { label: 'Equipe Aston', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
  humano_externo: { label: 'Externo', icon: Building2, color: 'text-green-600', bg: 'bg-green-50' },
  advogado: { label: 'Advogado', icon: Scale, color: 'text-purple-600', bg: 'bg-purple-50' },
  ata_reuniao: { label: 'Ata de reunião', icon: FileText, color: 'text-teal-600', bg: 'bg-teal-50' },
  ferramenta_automatica: { label: 'Automação', icon: Bot, color: 'text-slate-500', bg: 'bg-slate-100' },
};

const getTipoConfig = (tipo) => TIPOS_REMETENTE[tipo] || {
  label: tipo || 'desconhecido',
  icon: Wrench,
  color: 'text-slate-500',
  bg: 'bg-slate-100'
};

/**
 * Inbox de Aprendizado v3 (Sessão 4.6).
 *
 * Mudanças:
 * - Cada email mostra De/Para/CC resumidos no card
 * - Clique em "Abrir" expande drawer lateral à direita com formulário completo
 * - Vínculos multi-entidade: empresa + empresa_proprietaria + área + oferta + tipo conteúdo + advogado_representa
 */
const EmailsPendentesModal = ({ onClose }) => {
  const { user } = useAuth();
  const {
    emailsPendentes,
    stats,
    loading,
    classificando,
    ultimoResultado,
    error,
    refetch,
    classificarLote,
    aplicarVinculosCompletos,
    marcarComoIgnorado,
    marcarComoLixo,
    dominiosEmpresa,
  } = useEmailsPendentes();

  // Listas para os selects do drawer
  const { areas } = useAreas();
  const { ofertas } = useOfertas();
  const { contatos } = useContacts();

  // Deriva lista de empresas a partir dos contatos (evita fetch extra)
  const empresas = useMemo(() => {
    const map = new Map();
    (contatos || []).forEach(c => {
      if (c.empresas?.id && !map.has(c.empresas.id)) {
        map.set(c.empresas.id, c.empresas);
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.empresa || '').localeCompare(b.empresa || '')
    );
  }, [contatos]);

  // Mapa domínio(lower)→empresa, só com domínios que TÊM empresa vinculada
  // (empresa_id é nullable em dominios_empresa). Fonte da resolução. SPEC §3.
  const dominioEmpresaMap = useMemo(() => {
    const m = new Map();
    (dominiosEmpresa || []).forEach((d) => {
      if (!d?.dominio || !d?.empresa_id) return;
      const key = String(d.dominio).toLowerCase().trim();
      if (!m.has(key)) {
        m.set(key, { empresa_id: d.empresa_id, empresa_nome: d.empresas?.empresa || '(empresa)' });
      }
    });
    return m;
  }, [dominiosEmpresa]);

  const [batchSize, setBatchSize] = useState(20);
  const [filtroTipo, setFiltroTipo] = useState(null);
  const [emailSelecionado, setEmailSelecionado] = useState(null);
  const [localError, setLocalError] = useState(null);
  const [domainFilter, setDomainFilter] = useState(ALL_FILTER);
  // Snapshot do preview do gate de confirmação; modal aberto quando != null.
  const [previewVinculo, setPreviewVinculo] = useState(null);
  const sel = useBulkSelection();

  const handleClassificar = async () => {
    setLocalError(null);
    try {
      await classificarLote(batchSize);
    } catch (err) {
      setLocalError(err.message);
    }
  };

  const handleAplicarVinculos = async (emailId, vinculos) => {
    await aplicarVinculosCompletos(emailId, vinculos, user?.email);
    setEmailSelecionado(null); // fecha drawer após aplicar
  };

  const handleIgnorar = async (emailId) => {
    await marcarComoIgnorado(emailId, user?.email);
    setEmailSelecionado(null);
  };

  const handleLixo = async (emailId) => {
    await marcarComoLixo(emailId, user?.email);
    setEmailSelecionado(null);
  };

  const emailsVisiveis = filtroTipo
    ? emailsPendentes.filter(e => e.remetente_tipo === filtroTipo)
    : emailsPendentes;

  // Chips por domínio: top N por contagem + "Outros (M)" agrupa o resto
  const { domainChips, emailsFiltradosDominio, topDomains } = useMemo(() => {
    const counts = new Map();
    for (const e of emailsVisiveis) {
      const d = extractDomain(e.from_email);
      if (!d) continue;
      counts.set(d, (counts.get(d) || 0) + 1);
    }
    const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, TOP_DOMAINS);
    const rest = sorted.slice(TOP_DOMAINS);
    const restTotal = rest.reduce((acc, [, n]) => acc + n, 0);
    const topSet = new Set(top.map(([d]) => d));

    const chips = [
      { key: ALL_FILTER, label: 'Todos', count: emailsVisiveis.length },
      ...top.map(([d, n]) => ({ key: `domain:${d}`, label: `@${d}`, count: n })),
    ];
    if (restTotal > 0) chips.push({ key: OUTROS_FILTER, label: 'Outros', count: restTotal });

    let filtered;
    if (domainFilter === ALL_FILTER) {
      filtered = emailsVisiveis;
    } else if (domainFilter === OUTROS_FILTER) {
      filtered = emailsVisiveis.filter((e) => {
        const d = extractDomain(e.from_email);
        return !!d && !topSet.has(d);
      });
    } else if (domainFilter.startsWith('domain:')) {
      const target = domainFilter.slice('domain:'.length);
      filtered = emailsVisiveis.filter((e) => extractDomain(e.from_email) === target);
    } else {
      filtered = emailsVisiveis;
    }
    return { domainChips: chips, emailsFiltradosDominio: filtered, topDomains: topSet };
  }, [emailsVisiveis, domainFilter]);

  // void no-op pra silenciar unused-var (topDomains usado dentro do useMemo, ESLint OK)
  void topDomains;

  const handleDomainFilterChange = (key) => {
    setDomainFilter(key);
    sel.deselectAll();
  };

  // Quando muda filtroTipo (chips legados de remetente), também limpa seleção
  const handleFiltroTipoChange = (tipo) => {
    setFiltroTipo(tipo);
    sel.deselectAll();
    setDomainFilter(ALL_FILTER); // reset filtro de domínio também
  };

  // Abre o gate de confirmação (D-11): calcula o preview a partir do SNAPSHOT da
  // seleção atual e o guarda em estado. NÃO grava nada (SPEC §4.1/§4.2).
  // Capturamos o snapshot aqui porque o BulkActionBar limpa a seleção ao concluir
  // o onClick; o apply usa os ids do preview, não a seleção viva.
  const abrirPreviewVinculo = () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    const byId = new Map((emailsPendentes || []).map((e) => [e.id, e]));
    const emails = ids.map((id) => byId.get(id)).filter(Boolean);
    setPreviewVinculo(computeVinculoPreview(emails, dominioEmpresaMap));
  };

  // Apply do gate: grava SOMENTE empresa_id, em chunks, espelhando bulkLixo.
  // NÃO toca contato_id / acao_humana / oferta_id / area_mencionada_id /
  // empresa_proprietaria_id (P25 + SPEC §0/§4.4). Vincular empresa NÃO resolve o
  // email — ele segue pendente para receber oferta/área/tipo depois.
  const bulkVincularEmpresa = async (grupos) => {
    for (const grupo of grupos) {
      for (let i = 0; i < grupo.emailIds.length; i += BULK_CHUNK) {
        const chunk = grupo.emailIds.slice(i, i + BULK_CHUNK);
        const { error: updErr } = await supabase
          .from('emails_gmail')
          .update({ empresa_id: grupo.empresa_id })
          .in('id', chunk);
        if (updErr) {
          setLocalError(`Erro ao vincular empresa (lote): ${updErr.message}`);
          await refetch();
          throw updErr;
        }
      }
    }
    await refetch();
    setLocalError(null);
    sel.deselectAll();
    setPreviewVinculo(null);
  };

  const handleConfirmVinculo = async () => {
    if (!previewVinculo) return;
    await bulkVincularEmpresa(previewVinculo.grupos);
  };

  // Bulk Lixo: replicação fiel de marcarComoLixo (com prefixo "humano:")
  const bulkLixo = async () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    const acaoPor = `humano:${user?.email || 'desconhecido'}`;
    const acaoEm = new Date().toISOString();
    for (let i = 0; i < ids.length; i += BULK_CHUNK) {
      const chunk = ids.slice(i, i + BULK_CHUNK);
      const { error: updErr } = await supabase
        .from('emails_gmail')
        .update({ acao_humana: 'lixo', acao_humana_em: acaoEm, acao_humana_por: acaoPor })
        .in('id', chunk);
      if (updErr) {
        setLocalError(`Erro ao marcar lixo (lote): ${updErr.message}`);
        await refetch();
        throw updErr;
      }
    }
    await refetch();
    setLocalError(null);
  };

  // Bulk Ignorar: replicação fiel de marcarComoIgnorado (sem prefixo "humano:")
  const bulkIgnorar = async () => {
    const ids = sel.getSelectedIds();
    if (ids.length === 0) return;
    const acaoPor = user?.email || 'desconhecido';
    const acaoEm = new Date().toISOString();
    for (let i = 0; i < ids.length; i += BULK_CHUNK) {
      const chunk = ids.slice(i, i + BULK_CHUNK);
      const { error: updErr } = await supabase
        .from('emails_gmail')
        .update({ acao_humana: 'ignorado', acao_humana_em: acaoEm, acao_humana_por: acaoPor })
        .in('id', chunk);
      if (updErr) {
        setLocalError(`Erro ao ignorar (lote): ${updErr.message}`);
        await refetch();
        throw updErr;
      }
    }
    await refetch();
    setLocalError(null);
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-[95vw] 2xl:max-w-7xl max-h-[95vh] flex flex-col">

          {/* Header */}
          <div className="p-5 border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-amber-100 p-2 rounded-lg">
                  <Inbox className="text-amber-600" size={22} />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">Inbox de Aprendizado</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {stats.total} pendentes · {stats.com_area_detectada} com área detectada · {stats.sem_sinal} sem sinal
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {emailsFiltradosDominio.length > 0 && (
                  <SelectAllCheckbox
                    totalCount={emailsFiltradosDominio.length}
                    selectedCount={sel.count}
                    onSelectAll={() => sel.selectAll(emailsFiltradosDominio.map((e) => e.id))}
                    onDeselectAll={sel.deselectAll}
                    noun="visíveis"
                  />
                )}
                <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                  <X size={22} />
                </button>
              </div>
            </div>

            <div className="mt-3 bg-indigo-50 border border-indigo-200 rounded-md p-2.5 text-xs text-indigo-900 flex items-start gap-1.5">
              <Info size={12} className="flex-shrink-0 mt-0.5" />
              <span>
                Clique em <strong>"Abrir"</strong> em qualquer email para ver conteúdo completo e vincular a empresa/proprietária/área/oferta.
                Ou use <strong>"Classificar com IA"</strong> para processar em lote.
              </span>
            </div>

            {/* Context Loop AI-native Fase 1 (2026-04-26): ensina a IA sobre relacoes de negocio */}
            <div className="mt-3">
              <AIContextChat
                scope={{
                  screen: 'inbox',
                  email_ids: (emailsVisiveis || []).slice(0, 20).map(e => e.id),
                }}
                onActionsApplied={refetch}
                defaultCollapsed={true}
              />
            </div>

            {/* Controles IA */}
            <div className="mt-3 flex items-center gap-2">
              <label className="text-xs text-slate-600">Classificar próximos via IA:</label>
              <select
                value={batchSize}
                onChange={(e) => setBatchSize(Number(e.target.value))}
                className="text-xs px-2 py-1 border border-slate-300 rounded"
                disabled={classificando}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                onClick={handleClassificar}
                disabled={classificando || stats.total === 0}
                className="px-3 py-1.5 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1.5"
              >
                {classificando ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
                {classificando ? 'Classificando...' : 'Classificar com IA'}
              </button>
            </div>

            {/* Filtros por tipo */}
            {Object.keys(stats.por_remetente_tipo).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5 items-center">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">Filtrar:</span>
                <button
                  onClick={() => handleFiltroTipoChange(null)}
                  className={`text-[11px] px-2 py-0.5 rounded ${filtroTipo === null ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
                >
                  Todos ({stats.total})
                </button>
                {Object.entries(stats.por_remetente_tipo).map(([tipo, count]) => {
                  const cfg = getTipoConfig(tipo);
                  const Icon = cfg.icon;
                  const active = filtroTipo === tipo;
                  return (
                    <button
                      key={tipo}
                      onClick={() => handleFiltroTipoChange(active ? null : tipo)}
                      className={`text-[11px] px-2 py-0.5 rounded flex items-center gap-1 ${
                        active ? 'bg-slate-700 text-white' : `${cfg.bg} ${cfg.color} hover:opacity-80`
                      }`}
                    >
                      <Icon size={10} /> {cfg.label} ({count})
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {ultimoResultado && (
            <div className="mx-5 mt-3 p-3 bg-green-50 border border-green-200 rounded flex-shrink-0">
              <div className="flex items-start gap-2">
                <Check className="text-green-600 flex-shrink-0 mt-0.5" size={14} />
                <div className="flex-grow text-xs text-green-900">
                  <div className="font-semibold">
                    Classificação IA concluída — {ultimoResultado.total ?? ultimoResultado.stats?.total ?? 0} processados, {ultimoResultado.classified ?? ultimoResultado.stats?.classificados ?? 0} vinculados
                    {ultimoResultado.propagated > 0 && <span>, {ultimoResultado.propagated} por propagação de thread</span>}
                  </div>
                  {ultimoResultado.strategy_breakdown && (
                    <div className="mt-0.5 text-[10px] text-green-700">
                      Estratégias: {ultimoResultado.strategy_breakdown.thread || 0} por thread ·
                      {' '}{ultimoResultado.strategy_breakdown.rule || 0} por regra aprendida ·
                      {' '}{ultimoResultado.strategy_breakdown.claude || 0} por IA
                      {ultimoResultado.duration_ms ? ` · ${(ultimoResultado.duration_ms / 1000).toFixed(1)}s` : ''}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {(error || localError) && (
            <div className="mx-5 mt-3 p-2.5 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={12} />
              <span className="text-xs text-red-800">{error || localError}</span>
            </div>
          )}

          {/* Lista */}
          <div className="flex-grow overflow-y-auto p-5">
            {loading && (
              <div className="text-center py-10 text-sm text-slate-500">Carregando...</div>
            )}

            {!loading && emailsVisiveis.length > 0 && (
              <div className="mb-3">
                <FilterChips
                  chips={domainChips}
                  activeKey={domainFilter}
                  onChange={handleDomainFilterChange}
                />
              </div>
            )}

            {!loading && emailsVisiveis.length === 0 && (
              <div className="text-center py-10">
                <Check className="mx-auto text-green-500 mb-2" size={32} />
                <div className="text-sm font-medium text-slate-700">
                  {filtroTipo ? 'Nenhum email desse tipo pendente.' : 'Inbox limpa!'}
                </div>
              </div>
            )}

            {!loading && emailsVisiveis.length > 0 && emailsFiltradosDominio.length === 0 && (
              <div className="bg-white border border-slate-200 rounded p-6 text-center">
                <p className="text-xs text-slate-500">
                  Nenhum email neste filtro de domínio.{' '}
                  <button
                    onClick={() => handleDomainFilterChange(ALL_FILTER)}
                    className="text-blue-600 hover:underline"
                  >
                    Limpar filtro
                  </button>
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              {emailsFiltradosDominio.map(email => (
                <EmailCard
                  key={email.id}
                  email={email}
                  onAbrir={() => setEmailSelecionado(email)}
                  selected={sel.isSelected(email.id)}
                  onToggleSelect={() => sel.toggle(email.id)}
                />
              ))}
            </div>
          </div>

          <BulkActionBar
            count={sel.count}
            onClear={sel.deselectAll}
            className="!z-[10002]"
            actions={[
              {
                label: 'Vincular empresa',
                icon: Building2,
                color: 'blue',
                // SEM confirmMessage — abre modal de preview (gate D-11), não window.confirm.
                onClick: () => { abrirPreviewVinculo(); },
              },
              {
                label: 'Ignorar selecionados',
                icon: EyeOff,
                color: 'gray',
                confirmMessage: (n) => `Ignorar ${n} email${n === 1 ? '' : 's'}?`,
                onClick: bulkIgnorar,
              },
              {
                label: 'Marcar como Lixo',
                icon: Trash2,
                color: 'red',
                confirmMessage: (n) => `Marcar ${n} email${n === 1 ? '' : 's'} como lixo?`,
                onClick: bulkLixo,
              },
            ]}
          />

          <div className="p-3 border-t border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-between text-[10px] text-slate-500">
            <span>Clique em "Abrir" para vincular email a oferta/empresa/área/proprietária.</span>
            <button onClick={onClose} className="px-3 py-1 text-xs text-slate-600 hover:bg-slate-200 rounded">
              Fechar
            </button>
          </div>
        </div>
      </div>

      {/* Drawer lateral — renderiza acima do modal */}
      {emailSelecionado && (
        <EmailDetailDrawer
          email={emailSelecionado}
          empresas={empresas}
          areas={areas}
          ofertas={ofertas}
          contatosAdvogados={(contatos || []).filter(c => c.tipo_contato === 'consultor')}
          onAplicarVinculos={handleAplicarVinculos}
          onIgnorar={handleIgnorar}
          onLixo={handleLixo}
          onClose={() => setEmailSelecionado(null)}
        />
      )}

      {/* Gate de confirmação da ação de lote "Vincular empresa" (D-11) */}
      {previewVinculo && (
        <BulkVincularEmpresaModal
          preview={previewVinculo}
          onConfirm={handleConfirmVinculo}
          onClose={() => setPreviewVinculo(null)}
        />
      )}
    </>
  );
};

/**
 * Card compacto de email na lista.
 * Mostra De/Para/CC + empresa + área detectada + botão "Abrir" para ver detalhe.
 */
const EmailCard = ({ email, onAbrir, selected, onToggleSelect }) => {
  const cfg = getTipoConfig(email.remetente_tipo);
  const Icon = cfg.icon;
  const empresaNome = email.empresas?.empresa || '(sem empresa)';
  const areaNome = email.areas?.nome;

  return (
    <div
      className={`p-2.5 bg-white border rounded hover:shadow-sm transition-all cursor-pointer group ${
        selected ? 'border-red-400 bg-red-50/30' : 'border-slate-200 hover:border-indigo-300'
      }`}
      onClick={onAbrir}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { e.stopPropagation(); if (onToggleSelect) onToggleSelect(); }}
          aria-label="Selecionar email"
          className="cursor-pointer w-5 h-5 mt-0.5 accent-red-600 flex-shrink-0 rounded"
        />
        <div className="flex-grow min-w-0">
          {/* Linha 1: tipo + empresa + direção + data */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`${cfg.bg} ${cfg.color} text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1`}>
              <Icon size={10} /> {cfg.label}
            </span>
            <span className="text-xs font-semibold text-slate-800 truncate">
              {empresaNome}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
              {email.direcao === 'entrada' ? '📥' : '📤'}
            </span>
            <span className="text-[10px] text-slate-400 ml-auto flex-shrink-0">
              {formatDate(email.data_envio)}
            </span>
          </div>

          {/* Linha 2: assunto */}
          <div className="text-xs font-medium text-slate-800 truncate mt-1">
            {email.subject || '(sem assunto)'}
          </div>

          {/* Linhas 3-5: De / Para / CC (resumido) */}
          <div className="text-[11px] text-slate-600 mt-1 space-y-0.5">
            <div className="truncate"><span className="text-slate-400">De:</span> {email.from_email}</div>
            {email.to_emails && email.to_emails.length > 0 && (
              <div className="truncate">
                <span className="text-slate-400">Para:</span> {
                  email.to_emails.length > 2 
                    ? `${email.to_emails.slice(0, 2).join(', ')} +${email.to_emails.length - 2}`
                    : email.to_emails.join(', ')
                }
              </div>
            )}
            {email.cc_emails && email.cc_emails.length > 0 && (
              <div className="truncate text-slate-500">
                <span className="text-slate-400">CC:</span> {
                  email.cc_emails.length > 2 
                    ? `${email.cc_emails.slice(0, 2).join(', ')} +${email.cc_emails.length - 2}`
                    : email.cc_emails.join(', ')
                }
              </div>
            )}
          </div>

          {/* Linha 6: área detectada (sinal semântico) */}
          {areaNome && (
            <div className="text-[11px] text-indigo-700 mt-1 flex items-center gap-1 truncate">
              <Sparkles size={10} /> Menciona: <strong>{areaNome}</strong>
            </div>
          )}

          {/* Botão abrir */}
          <div className="mt-1.5 flex items-center justify-end">
            <button
              onClick={(e) => { e.stopPropagation(); onAbrir(); }}
              className="text-[11px] px-2 py-0.5 bg-indigo-600 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1"
            >
              <Eye size={10} /> Abrir
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailsPendentesModal;
